import { ScenarioType } from "@mutiny-group/sbo-openapi-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { components } from "@/@types/api";
import { INSIGHTS_BACKEND_URL } from "@/lib/axios";
import { MutationConfig } from "@/lib/react-query";
import { axiosApi } from "@/Utils";

import { useScenarioBuilderClient } from "../../api/hooks/useScenarioBuilderClient";
import { createScenarioV2 } from "./v2/createScenario";

export type CreateScenarioDTO = components["schemas"]["NewCreateScenarioRequest"];
export type CreateScenarioResponse = components["schemas"]["NewCreateScenarioResponse"];
export type CreateScenarioOptions = Partial<Omit<CreateScenarioDTO, "scenarioType" | "createdBy">>;

export const createScenario = (scenario: CreateScenarioDTO): Promise<CreateScenarioResponse> => {
  return axiosApi.post("/api/v2/scenarios-new", scenario, {
    baseURL: INSIGHTS_BACKEND_URL,
  });
};

type UseCreateScenarioOptions = {
  isScenariosApiEnabled?: boolean;
  config?: MutationConfig<any>;
};

export const useCreateScenario = ({
  isScenariosApiEnabled,
  config,
}: UseCreateScenarioOptions = {}) => {
  const queryClient = useQueryClient();
  const scenarioBuilderClient = useScenarioBuilderClient();

  const createScenarioMutationFn = useMemo(() => {
    if (isScenariosApiEnabled) {
      return (scenario: CreateScenarioDTO) =>
        createScenarioV2(scenarioBuilderClient, scenario.scenarioType as ScenarioType);
    }
    return createScenario;
  }, [isScenariosApiEnabled, scenarioBuilderClient]);

  return useMutation({
    onSuccess: async () => {
      if (isScenariosApiEnabled) {
        await queryClient.invalidateQueries({ queryKey: ["scenarios-v2"] });
      } else {
        await queryClient.invalidateQueries({ queryKey: ["scenarios-new"] });
        await queryClient.invalidateQueries({ queryKey: ["is-optimizer-running"] });
      }
    },
    onError: (error: any) => {
      console.error("useCreateScenario onError", error);
    },
    ...config,
    mutationFn: createScenarioMutationFn,
  });
};
